/**
 * Camera basis / rotation helpers.
 * Ported from supersplat-viewer/src/cameras/camera-utils.ts (orbit bits removed).
 */

import { math, Quat, Vec3 } from 'playcanvas';
import { damp, mod } from './math-utils';
import type { CameraFrame } from './types';

/** Shared damping factor for controller smoothing. damp(d,dt) = 1 - d^(dt*1000). */
const DEFAULT_CONTROLLER_DAMPING = 0.95;

const rotation = new Quat();

/**
 * Apply a CameraFrame rotate delta [yaw, pitch, roll] to camera Euler angles
 * [pitch, yaw, roll] (degrees), clamping pitch.
 */
const applyFrameRotation = (
    angles: Vec3,
    rotate: readonly number[],
    minPitch = -90,
    maxPitch = 90
) => {
    angles.x -= rotate[1];
    angles.y -= rotate[0];
    angles.z = 0;
    angles.x = math.clamp(angles.x, minPitch, maxPitch);
    return angles;
};

/** Camera-relative basis vectors from Euler angles (degrees). */
const setCameraBasis = (angles: Vec3, forward: Vec3, right: Vec3, up: Vec3) => {
    rotation.setFromEulerAngles(angles);
    rotation.transformVector(Vec3.FORWARD, forward);
    rotation.transformVector(Vec3.RIGHT, right);
    rotation.transformVector(Vec3.UP, up);
};

/** Yaw-only horizontal basis vectors (for walk movement). */
const setYawBasis = (yaw: number, forward: Vec3, right: Vec3) => {
    rotation.setFromEulerAngles(0, yaw, 0);
    rotation.transformVector(Vec3.FORWARD, forward);
    rotation.transformVector(Vec3.RIGHT, right);
};

/** Build a world-space offset from local (right, up, forward) movement. */
const setBasisOffset = (
    out: Vec3, x: number, y: number, z: number,
    forward: Vec3, right: Vec3, up: Vec3
) => {
    out.set(
        right.x * x + up.x * y + forward.x * z,
        right.y * x + up.y * y + forward.y * z,
        right.z * x + up.z * y + forward.z * z
    );
    return out;
};

/** Lerp Euler angles toward a target using frame-rate-independent shortest-path. */
const dampAngles = (angles: Vec3, target: Vec3, damping: number, dt: number) => {
    if (dt <= 0) return angles;
    const t = damp(damping, dt);
    angles.y = mod(angles.y, 360);
    target.y = mod(target.y, 360);
    angles.x = math.lerpAngle(angles.x, target.x, t);
    angles.y = math.lerpAngle(angles.y, target.y, t);
    return angles;
};

/** Drain any pending input deltas in the frame (read & discard). */
const drainInputFrame = (frame: CameraFrame) => {
    frame.read();
};

export {
    DEFAULT_CONTROLLER_DAMPING,
    applyFrameRotation,
    dampAngles,
    drainInputFrame,
    setBasisOffset,
    setCameraBasis,
    setYawBasis
};
